import { signal } from "@preact/signals";
import { trackException } from "~/web/src/analytics";

// One page-wide error queue: anything async that fails out of band (uploads,
// fetches) reports here and the ErrorModal shows the queue until dismissed.

export interface Cta {
  label: string;
  href: string;
}

/** One entry in the modal: the heading (default "Something went wrong"), the message, and
 * optionally a button that takes the user where the fix is (Pro, for a file over the free limit). */
export interface ReportedError {
  title?: string;
  text: string;
  cta?: Cta;
}

/** An error that knows how it wants to be shown; anything else shows as its text alone. */
export class ShownError extends Error {
  constructor(
    message: string,
    public readonly title?: string,
    public readonly cta?: Cta,
  ) {
    super(message);
  }
}

export const errors = signal<ReportedError[]>([]);

/** A fetch that never reached the server (offline, a dropped connection) rejects with a
 * TypeError; pages say the site's own sentence, not the browser's "Failed to fetch". */
export const OFFLINE = "Couldn't reach Featurette — check your connection and try again.";

/** The message to show for a caught value: an Error's message, anything else stringified. */
export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function reportError(e: unknown): void {
  const shown: ReportedError =
    e instanceof ShownError
      ? { title: e.title, text: e.message, cta: e.cta }
      : { text: errorText(e) };
  errors.value = [...errors.value, shown];
  trackException(e);
}

export function dismissErrors(): void {
  errors.value = [];
}
